import { useCallback, useMemo, useState } from 'react'
import AppStore from '@v4/store/appStore'
import { getSectionTheme } from '@v4/utils/theme'
import useThemeSettings from '@v4/hooks/useThemeSettings'
import PageBuilderSelector from '@v4/utils/selectors/PageBuilderSelector'
import PageData from '@v4/utils/data/PageData'
import { usePageMetaData } from '@v4/components/common/Page'
import useGeneralMetaData from '@v4/hooks/useGeneralMetaData'
import PageTemplateData from '@v4/utils/data/PageTemplateData'
import PageBuilderModel from '@v4/store/builderStore/models/PageBuilder.model'
import { useStoreState } from 'easy-peasy'
import ScorecardSelector from '@v4/utils/selectors/ScorecardSelector'
import QuestionsBuilderSelector from '@v4/utils/selectors/QuestionsBuilderSelector'
import ThemeSelector from '@v4/utils/selectors/ThemeSelector'
import { createSelectAdjacentSectionIds } from './SectionItem.selectors'
import useStyles from './SectionItem.styled'

export const useSectionActionsState = withThemeButton => {
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const { setThemeButtonVisible } = AppStore.useStoreActions(state => state.general)

	const [showSectionActions, setShowSectionActions] = useState(false)

	const handleMouseEnter = useCallback(() => {
		setShowSectionActions(true)
		if (withThemeButton) {
			setThemeButtonVisible(true)
		}
	}, [withThemeButton, setThemeButtonVisible])

	const handleMouseLeave = useCallback(() => {
		setShowSectionActions(false)
		if (withThemeButton) {
			setThemeButtonVisible(false)
		}
	}, [withThemeButton, setThemeButtonVisible])

	return useMemo(
		() => ({
			visible: isBuilderMode && showSectionActions,
			handleMouseEnter: isBuilderMode ? handleMouseEnter : undefined,
			handleMouseLeave: isBuilderMode ? handleMouseLeave : undefined,
		}),
		[handleMouseEnter, handleMouseLeave, isBuilderMode, showSectionActions],
	)
}

export const useSectionItem = ({
	active,
	className,
	collection,
	id,
	index,
	additionIndex,
	parentId,
	sectionsMap,
	first,
	last,
}) => {
	const isBuilderMode = AppStore.useStoreState(state => state.general.isBuilderMode)
	const isQuestionBuilderMode = PageBuilderSelector.useIsQuestionsMode()
	const pageType = PageBuilderSelector.useType()
	const sectionData = PageBuilderSelector.useSectionData(id)
	const template = PageBuilderSelector.useSectionTemplate(sectionData.key)
	const overrideMetaData = usePageMetaData()
	const metaData = useGeneralMetaData({ sectionId: id, sectionKey: sectionData.key, overrideMetaData })
	const themeSettings = useThemeSettings(metaData)
	const isPdf = PageData.isTypeResultPdf(pageType)
	const isQuestions = PageData.isTypeQuestions(pageType)
	const themeButtonVisible = AppStore.useStoreState(state => state.general.themeButtonVisible)
	const withThemeButton = isBuilderMode && isQuestionBuilderMode && template.with_theme_button
	const { updateGallery } = QuestionsBuilderSelector.useActions()
	const isThemeGalleryActive = ThemeSelector.useIsGalleryActive()
	const { updateGallery: updateThemeGallery } = ThemeSelector.useActions()

	const selectAdjacentSectionIds = useMemo(createSelectAdjacentSectionIds, [])

	const isSectionsLocked = ScorecardSelector.useIsSectionsLocked()

	const adjacentSectionIds = useStoreState(state => selectAdjacentSectionIds(state, collection, id))

	const prevSectionMarginBottomData = PageBuilderSelector.useMetaData({
		name: 'marginBottom',
		sectionId: adjacentSectionIds[0],
	})

	const nextSectionMarginTopData = PageBuilderSelector.useMetaData({
		name: 'marginTop',
		sectionId: adjacentSectionIds[1],
	})

	const sectionActions = useSectionActionsState(withThemeButton)
	const { setActiveSectionId, setSidebarMode, setAdditionalIndex } = PageBuilderSelector.useActions()

	const classes = useStyles({
		active,
		metaData,
		isBuilderMode,
	})
	const theme = useCallback(parentTheme => getSectionTheme(parentTheme, themeSettings, template.theme?.type), [
		template,
		themeSettings,
	])

	const section = sectionsMap[sectionData.parsedKey.group]
	const Content = section?.component
	const ContentElement = section?.getVariantComponent(template.theme?.variant)

	const handleStopPropagation = useCallback(event => {
		event.stopPropagation()
		event.preventDefault()
	}, [])

	const handleClick = useCallback(
		event => {
			handleStopPropagation(event)
			if (isBuilderMode && isThemeGalleryActive) {
				updateThemeGallery({ active: false })
			}
			if (isBuilderMode && isQuestionBuilderMode) {
				updateGallery({ active: false })
				return undefined
			}

			return setActiveSectionId({ id })
		},
		[
			handleStopPropagation,
			isBuilderMode,
			isQuestionBuilderMode,
			setActiveSectionId,
			id,
			isThemeGalleryActive,
			updateThemeGallery,
			updateGallery,
		],
	)

	const handleAdd = useCallback(() => {
		setSidebarMode(PageBuilderModel.SIDEBAR_MODE.ADDITION)
		setAdditionalIndex(additionIndex)
	}, [additionIndex, setAdditionalIndex, setSidebarMode])

	const prevSectionMarginBottom = prevSectionMarginBottomData?.toNumber()
	const nextSectionMarginTop = nextSectionMarginTopData?.toNumber()

	const marginTop = metaData.marginTop?.toNumber()
	const marginBottom = metaData.marginBottom?.toNumber()

	const relatedSections = useMemo(() => {
		const sections = [undefined, undefined]

		if (collection !== PageTemplateData.SORT_GROUPS.MAIN) {
			return sections
		}

		if (first) {
			sections[0] = PageTemplateData.SORT_GROUPS.HEADER
		}

		if (last) {
			sections[1] = PageTemplateData.SORT_GROUPS.FOOTER
		}

		return sections
	}, [collection, first, last])

	return [
		{
			active: isBuilderMode && active,
			classes,
			className,
			collection,
			Content,
			ContentElement,
			index,
			additionIndex,
			isPdf,
			parentId,
			sectionData,
			theme,
			withDivider: isBuilderMode && isPdf,
			metaData,
			template,
			relatedSections,
			showSectionActions:
				isBuilderMode &&
				!isQuestionBuilderMode &&
				sectionActions.visible &&
				collection !== PageTemplateData.SECTION_TYPES.POPUP,
			id,
			sectionActions,
			showAddSectionBtn:
				isBuilderMode &&
				!isQuestionBuilderMode &&
				!isPdf &&
				PageTemplateData.isSortGroupMain(collection) &&
				sectionActions.visible &&
				!isQuestions &&
				!isSectionsLocked,
			hoverOffset: {
				top: (marginTop < 0 ? marginTop : 0) - (prevSectionMarginBottom < 0 ? prevSectionMarginBottom : 0),
				bottom: (marginBottom < 0 ? marginBottom : 0) - (nextSectionMarginTop < 0 ? nextSectionMarginTop : 0),
			},
			isSectionsLocked,
			withThemeButton,
			themeButtonVisible: withThemeButton && themeButtonVisible,
			isQuestionBuilderMode,
		},
		{
			handleClick: isBuilderMode ? handleClick : undefined,
			handleAdd,
			handleStopPropagation,
		},
	]
}
